import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, LogIn, AlertCircle, Users, Calendar, Phone, Mail,
  Tag, Filter, Download, ChevronDown, ChevronUp, MessageCircle,
  Clock, CheckCircle, XCircle, UserCheck, DollarSign, Loader2,
  Settings, Database, Play, FileDown, AlertTriangle, Flame,
  ThermometerSun, Snowflake, Activity, BarChart2, RefreshCw,
  PhoneCall, Timer, FileText, Zap, Link2, Unlink, Globe, Search,
  Save, Edit2, X, FlaskConical, MessageSquare, KeyRound, ChevronRight, Mic
} from 'lucide-react';
import AdminLabPage from './AdminLabPage';
import { AdminSupportTab } from '../components/crm/AdminSupportTab';
import {
  CallFormAnswers, CallTimeField, MeetLinkField, NotesField, OutcomeButtons,
  ChangePasswordModal, FormStatusBadge, formatIst, LANGUAGE_LABELS, OUTCOME_LABELS,
} from '../components/crm/CrmLeadFields';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tier colors and icons
const TIER_CONFIG = {
  HOT: { label: 'Hot', color: '#EF4444', bgColor: 'bg-red-500/10', icon: Flame },
  WARM: { label: 'Warm', color: '#F59E0B', bgColor: 'bg-amber-500/10', icon: ThermometerSun },
  COLD: { label: 'Cold', color: '#06b6d4', bgColor: 'bg-cyan-500/10', icon: Snowflake },
};

// Call tab config
const CALL_TAB_CONFIG = {
  awaiting: { label: 'Awaiting Time', color: '#F59E0B', icon: Timer },
  booked: { label: 'Booked', color: '#06b6d4', icon: Calendar },
  needs_outcome: { label: 'Needs Outcome', color: '#7c3aed', icon: PhoneCall },
  done: { label: 'Done', color: '#10B981', icon: CheckCircle },
};

export default function AdminCRMPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Main tabs: leads, calls, settings
  const [mainTab, setMainTab] = useState('leads');
  
  // Leads state
  const [leads, setLeads] = useState([]);
  const [leadsStats, setLeadsStats] = useState(null);
  const [expandedLead, setExpandedLead] = useState(null);
  const [leadActivities, setLeadActivities] = useState({});
  
  // Filters
  const [filters, setFilters] = useState({
    tier: 'all',
    source: '',
    status: '',
    platform: '',
    language: '',
    dateFrom: '',
    dateTo: '',
    formStatus: '',
    showMerged: false,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showMergePanel, setShowMergePanel] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // Calls state
  const [callLeads, setCallLeads] = useState([]);
  const [callsStats, setCallsStats] = useState(null);
  const [callsTabFilter, setCallsTabFilter] = useState('awaiting');
  
  // Settings state
  const [dryRunResult, setDryRunResult] = useState(null);
  const [mergeResult, setMergeResult] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [mergeState, setMergeState] = useState(null);
  
  // Scoring config
  const [scoringConfig, setScoringConfig] = useState(null);
  const [editingScoring, setEditingScoring] = useState(false);
  const [scoringForm, setScoringForm] = useState({});

  const getAuthHeader = useCallback(() => {
    const savedAuth = localStorage.getItem('crmAuth');
    if (savedAuth) {
      return { 'Authorization': 'Basic ' + savedAuth };
    }
    return { 'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`) };
  }, [credentials]);

  const savedCredentials = () => {
    const saved = localStorage.getItem('crmAuth');
    if (!saved) return credentials;
    const decoded = atob(saved);
    const i = decoded.indexOf(':');
    return { username: decoded.slice(0, i), password: decoded.slice(i + 1) };
  };

  const refreshCurrentTab = () => (mainTab === 'calls' ? fetchCalls() : fetchLeads());

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/crm/leads`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        setIsAuthenticated(true);
        localStorage.setItem('crmAuth', btoa(`${credentials.username}:${credentials.password}`));
        fetchLeads();
      } else {
        setAuthError('Invalid username or password');
      }
    } catch (error) {
      setAuthError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.tier !== 'all') params.append('tab', filters.tier);
      if (filters.source) params.append('source', filters.source);
      if (filters.status) params.append('status', filters.status);
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.language) params.append('language', filters.language);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.formStatus) params.append('form_status', filters.formStatus);
      if (filters.showMerged) params.append('show_merged', 'true');

      const url = `${API_URL}/api/admin/crm/leads?${params.toString()}`;
      const res = await fetch(url, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
        setLeadsStats(data.stats);
      } else if (res.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('crmAuth');
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, getAuthHeader]);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/admin/crm/calls?tab=${callsTabFilter}`;
      const res = await fetch(url, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setCallLeads(data.leads || []);
        setCallsStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  }, [callsTabFilter, getAuthHeader]);

  const fetchLeadActivities = async (leadId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/leads/${leadId}/activities`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setLeadActivities(prev => ({ ...prev, [leadId]: data.activities }));
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchMergeState = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/settings/merge-state`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setMergeState(data);
      }
    } catch (error) {
      console.error('Error fetching merge state:', error);
    }
  };

  const fetchScoringConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/settings/scoring`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setScoringConfig(data.config);
        setScoringForm(data.config);
      }
    } catch (error) {
      console.error('Error fetching scoring config:', error);
    }
  };

  const setCallTime = async (leadId, callTime) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/leads/${leadId}/call-time`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_time: callTime })
      });
      if (res.ok) {
        if (mainTab === 'calls') fetchCalls();
        else fetchLeads();
      }
    } catch (error) {
      console.error('Error setting call time:', error);
    }
  };

  const setCallOutcome = async (leadId, outcome) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/leads/${leadId}/call-outcome`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome })
      });
      if (res.ok) {
        if (mainTab === 'calls') fetchCalls();
        else fetchLeads();
      }
    } catch (error) {
      console.error('Error setting outcome:', error);
    }
  };

  const mergeLeads = async (leadId, otherId) => {
    if (!window.confirm('Merge these two leads into one?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/leads/${leadId}/merge-with/${otherId}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (res.ok) {
        fetchLeads();
        setExpandedLead(null);
      }
    } catch (error) {
      console.error('Error merging leads:', error);
    }
  };

  const markNotSame = async (leadId, otherId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/leads/${leadId}/not-same/${otherId}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (res.ok) {
        fetchLeads();
      }
    } catch (error) {
      console.error('Error marking as not same:', error);
    }
  };

  const startMergeSession = async () => {
    try {
      await fetch(`${API_URL}/api/admin/crm/settings/start-merge-session`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      fetchMergeState();
    } catch (error) {
      console.error('Error starting session:', error);
    }
  };

  const downloadBackup = () => {
    window.open(`${API_URL}/api/admin/crm/settings/backup`, '_blank');
    setTimeout(fetchMergeState, 1000);
  };

  const runDryRun = async () => {
    setSettingsLoading(true);
    setDryRunResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/settings/dry-run`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setDryRunResult(data);
        fetchMergeState();
      }
    } catch (error) {
      console.error('Error running dry run:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const runMerge = async () => {
    if (!window.confirm('Run the merge? This will create/update the unified leads collection.')) {
      return;
    }
    setSettingsLoading(true);
    setMergeResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/settings/run-merge`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setMergeResult(data);
        fetchMergeState();
        fetchLeads();
      } else {
        const error = await res.json();
        alert(error.detail || 'Merge failed');
      }
    } catch (error) {
      console.error('Error running merge:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveScoringConfig = async () => {
    setSettingsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/crm/settings/scoring`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ config: scoringForm })
      });
      if (res.ok) {
        const data = await res.json();
        setScoringConfig(data.config);
        setEditingScoring(false);
        fetchLeads();
        alert(`Scoring updated. ${data.leads_updated} leads recalculated.`);
      }
    } catch (error) {
      console.error('Error saving scoring:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const exportCSV = async () => {
    const res = await fetch(`${API_URL}/api/admin/crm/export`, { headers: getAuthHeader() });
    if (!res.ok) return;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `crm_leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Check saved auth on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('crmAuth');
    if (savedAuth) {
      fetch(`${API_URL}/api/admin/crm/leads`, {
        headers: { 'Authorization': 'Basic ' + savedAuth }
      })
        .then(res => {
          if (res.ok) {
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('crmAuth');
          }
        })
        .catch(() => localStorage.removeItem('crmAuth'));
    }
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (isAuthenticated) {
      if (mainTab === 'leads') fetchLeads();
      else if (mainTab === 'calls') fetchCalls();
    }
  }, [mainTab, isAuthenticated, fetchLeads, fetchCalls]);

  useEffect(() => {
    if (isAuthenticated && showMergePanel) {
      fetchMergeState();
      fetchScoringConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, showMergePanel]);

  // Refetch leads when filters change
  useEffect(() => {
    if (isAuthenticated && mainTab === 'leads') {
      fetchLeads();
    }
  }, [filters, isAuthenticated, mainTab, fetchLeads]);

  const handleExpandLead = (leadId) => {
    if (expandedLead === leadId) {
      setExpandedLead(null);
    } else {
      setExpandedLead(leadId);
      if (!leadActivities[leadId]) {
        fetchLeadActivities(leadId);
      }
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center px-6 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-strong rounded-2xl p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-14 h-14 rounded-xl bg-[#7c3aed]/15 flex items-center justify-center">
                <Users className="w-7 h-7 text-[#7c3aed]" />
              </div>
            </div>
            
            <h1 className="font-heading font-bold text-2xl text-white text-center mb-2">CRM Admin</h1>
            <p className="font-body text-white/50 text-sm text-center mb-6">Unified lead management</p>

            {authError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="font-body text-sm text-red-400">{authError}</span>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="font-body text-xs text-white/50 mb-1 block">Username</label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white font-body focus:outline-none focus:border-[#7c3aed]/50"
                  placeholder="Enter username"
                  required
                  data-testid="crm-admin-username"
                />
              </div>
              
              <div>
                <label className="font-body text-xs text-white/50 mb-1 block">Password</label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white font-body focus:outline-none focus:border-[#7c3aed]/50"
                  placeholder="Enter password"
                  required
                  data-testid="crm-admin-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-purple flex items-center justify-center gap-2 py-3"
                data-testid="crm-admin-login-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <LogIn size={16} />
                    Sign In
                  </>
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050508] pt-24 pb-12 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading font-bold text-2xl text-white mb-1">CRM Dashboard</h1>
              <p className="font-body text-white/50 text-sm">Unified lead management</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportCSV}
                className="btn-secondary text-sm flex items-center gap-2"
                data-testid="crm-export-csv"
              >
                <Download size={14} />
                Export
              </button>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="btn-secondary text-sm flex items-center gap-2"
                data-testid="crm-change-password"
              >
                <KeyRound size={14} />
                Password
              </button>
              <button
                onClick={() => {
                  setIsAuthenticated(false);
                  localStorage.removeItem('crmAuth');
                }}
                className="btn-secondary text-sm"
                data-testid="crm-logout"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Main Tabs */}
          <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
            {[
              { id: 'strategy_lab', label: 'Strategy Lab', icon: FlaskConical },
              { id: 'leads', label: 'Leads', icon: Users },
              { id: 'calls', label: 'Calls', icon: PhoneCall },
              { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
              { id: 'ai_support', label: 'AI Support', icon: Mic },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setMainTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  mainTab === tab.id
                    ? 'bg-[#7c3aed] text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
                data-testid={`crm-tab-${tab.id}`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* LEADS TAB */}
          {mainTab === 'leads' && (
            <>
              <button
                onClick={() => setShowMergePanel(v => !v)}
                className="mb-4 px-3 py-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 text-xs font-medium flex items-center gap-2"
                data-testid="crm-merge-scoring-toggle"
              >
                <Database size={14} />
                Merge &amp; scoring
                {showMergePanel ? <ChevronUp size={14} /> : <ChevronRight size={14} />}
              </button>
              {/* MERGE & SCORING PANEL */}
              {showMergePanel && (
            <div className="space-y-6 mb-6" data-testid="crm-merge-scoring-panel">
              {/* Merge Tools */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-heading font-bold text-lg text-white mb-4 flex items-center gap-2">
                  <Database size={20} />
                  CRM Merge Tools
                </h3>
                <p className="text-white/50 text-sm mb-4">
                  Merge strategy_lab_leads, tester_downloads, and call_leads into a unified 'leads' collection.
                </p>
                
                {/* Session Status */}
                {mergeState && (
                  <div className="p-3 rounded-lg bg-white/5 mb-4 text-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/60">Merge completed:</span>
                      <span className={mergeState.merge_completed ? 'text-[#10B981]' : 'text-white/40'}>
                        {mergeState.merge_completed ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/60">Backup downloaded (this session):</span>
                      <span className={mergeState.session?.backup_downloaded ? 'text-[#10B981]' : 'text-white/40'}>
                        {mergeState.session?.backup_downloaded ? '✓' : '✗'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">Dry run completed (this session):</span>
                      <span className={mergeState.session?.dry_run_completed ? 'text-[#10B981]' : 'text-white/40'}>
                        {mergeState.session?.dry_run_completed ? '✓' : '✗'}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="grid md:grid-cols-4 gap-4">
                  <button
                    onClick={startMergeSession}
                    className="flex flex-col items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
                  >
                    <RefreshCw className="w-6 h-6 text-white/60" />
                    <span className="font-medium text-white text-sm">New Session</span>
                  </button>
                  
                  <button
                    onClick={downloadBackup}
                    className="flex flex-col items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
                    data-testid="crm-download-backup"
                  >
                    <Download className="w-6 h-6 text-[#06b6d4]" />
                    <span className="font-medium text-white text-sm">1. Download Backup</span>
                  </button>
                  
                  <button
                    onClick={runDryRun}
                    disabled={settingsLoading}
                    className="flex flex-col items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all disabled:opacity-50"
                    data-testid="crm-dry-run"
                  >
                    {settingsLoading ? (
                      <Loader2 className="w-6 h-6 text-[#F59E0B] animate-spin" />
                    ) : (
                      <BarChart2 className="w-6 h-6 text-[#F59E0B]" />
                    )}
                    <span className="font-medium text-white text-sm">2. Dry Run</span>
                  </button>
                  
                  <button
                    onClick={runMerge}
                    disabled={settingsLoading || !mergeState?.session?.backup_downloaded || !mergeState?.session?.dry_run_completed}
                    className="flex flex-col items-center gap-3 p-4 rounded-xl bg-[#7c3aed]/10 border border-[#7c3aed]/20 hover:bg-[#7c3aed]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="crm-run-merge"
                  >
                    {settingsLoading ? (
                      <Loader2 className="w-6 h-6 text-[#7c3aed] animate-spin" />
                    ) : (
                      <Play className="w-6 h-6 text-[#7c3aed]" />
                    )}
                    <span className="font-medium text-white text-sm">3. Run Merge</span>
                  </button>
                </div>
              </div>

              {/* Dry Run Results */}
              {dryRunResult && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-xl p-6">
                  <h3 className="font-heading font-bold text-lg text-white mb-4">Dry Run Report</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6 mb-4">
                    <div className="p-4 rounded-lg bg-white/5">
                      <h4 className="text-white/60 text-sm mb-3">Before Merge</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/40">Strategy Lab:</span>
                          <span className="text-white">{dryRunResult.before.strategy_lab_leads}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/40">Tester:</span>
                          <span className="text-white">{dryRunResult.before.tester_downloads}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/40">Call:</span>
                          <span className="text-white">{dryRunResult.before.call_leads}</span>
                        </div>
                        <div className="flex justify-between font-bold border-t border-white/10 pt-2">
                          <span className="text-white">Total:</span>
                          <span className="text-white">{dryRunResult.before.total}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 rounded-lg bg-[#10B981]/10 border border-[#10B981]/20">
                      <h4 className="text-[#10B981] text-sm mb-3">After Merge</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/40">Unified leads:</span>
                          <span className="text-[#10B981] font-bold">{dryRunResult.after.unified_leads}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/40">Records merged:</span>
                          <span className="text-white">{dryRunResult.after.records_merged}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {dryRunResult.possible_duplicates_count > 0 && (
                    <div className="p-4 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20">
                      <h4 className="text-[#F59E0B] text-sm mb-2">Possible Duplicates (kept separate): {dryRunResult.possible_duplicates_count}</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {dryRunResult.possible_duplicates?.map((dup, idx) => (
                          <div key={idx} className="text-xs text-white/70">
                            <span className="text-white">{dup.name1}</span> vs <span className="text-white">{dup.name2}</span>
                            <span className="text-white/40 ml-2">(Phone: {dup.phone})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Merge Results */}
              {mergeResult && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-xl p-6 border border-[#10B981]/30">
                  <h3 className="font-heading font-bold text-lg text-[#10B981] mb-2 flex items-center gap-2">
                    <CheckCircle size={20} />
                    Merge Complete
                  </h3>
                  <p className="text-white">{mergeResult.message}</p>
                </motion.div>
              )}

              {/* Scoring Configuration */}
              <div className="glass rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-lg text-white flex items-center gap-2">
                    <BarChart2 size={20} />
                    Scoring Configuration
                  </h3>
                  {!editingScoring ? (
                    <button
                      onClick={() => setEditingScoring(true)}
                      className="btn-secondary text-sm flex items-center gap-2"
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingScoring(false);
                          setScoringForm(scoringConfig);
                        }}
                        className="btn-secondary text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveScoringConfig}
                        disabled={settingsLoading}
                        className="btn-purple text-sm flex items-center gap-2"
                      >
                        {settingsLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Save & Recalculate
                      </button>
                    </div>
                  )}
                </div>
                
                {scoringConfig && (
                  <div className="grid md:grid-cols-3 gap-4">
                    {/* Positive Points */}
                    <div className="space-y-3">
                      <h4 className="text-[#10B981] text-sm font-semibold">Positive Points</h4>
                      {[
                        ['call_form', 'Call form submitted'],
                        ['timeline_this_week', 'Timeline: This week'],
                        ['timeline_this_month', 'Timeline: This month'],
                        ['prop_100k_plus', 'Prop size: $100k+'],
                        ['prop_50k_100k', 'Prop size: $50k-100k'],
                        ['prop_25k_50k', 'Prop size: $25k-50k'],
                        ['capital_above_3k', 'Capital above $3k'],
                        ['platform_chosen', 'Platform chosen'],
                        ['tester_downloaded', 'Tester downloaded'],
                        ['tester_worked', 'Tester worked'],
                        ['whatsapp_tapped', 'WhatsApp tapped'],
                        ['call_time_set', 'Call time set'],
                        ['joined_interested', 'Outcome: Interested'],
                        ['strategy_lab_per_download', 'Strategy Lab (per download)'],
                        ['strategy_lab_max', 'Strategy Lab (max)'],
                        ['recent_activity', 'Recent activity (14 days)'],
                      ].map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between gap-2">
                          <span className="text-white/60 text-xs">{label}:</span>
                          {editingScoring ? (
                            <input
                              type="number"
                              value={scoringForm[key] || 0}
                              onChange={(e) => setScoringForm(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                              className="w-16 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs text-right"
                            />
                          ) : (
                            <span className="text-[#10B981] text-xs font-bold">+{scoringConfig[key]}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Negative Points */}
                    <div className="space-y-3">
                      <h4 className="text-red-400 text-sm font-semibold">Negative Points</h4>
                      {[
                        ['inactive_30_days', 'Inactive 30+ days'],
                        ['no_shows_or_opted_out', '2+ no-shows / opted out'],
                      ].map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between gap-2">
                          <span className="text-white/60 text-xs">{label}:</span>
                          {editingScoring ? (
                            <input
                              type="number"
                              value={scoringForm[key] || 0}
                              onChange={(e) => setScoringForm(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                              className="w-16 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs text-right"
                            />
                          ) : (
                            <span className="text-red-400 text-xs font-bold">{scoringConfig[key]}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Thresholds */}
                    <div className="space-y-3">
                      <h4 className="text-[#7c3aed] text-sm font-semibold">Tier Thresholds</h4>
                      {[
                        ['hot_threshold', 'Hot threshold (≥)'],
                        ['warm_threshold', 'Warm threshold (≥)'],
                      ].map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between gap-2">
                          <span className="text-white/60 text-xs">{label}:</span>
                          {editingScoring ? (
                            <input
                              type="number"
                              value={scoringForm[key] || 0}
                              onChange={(e) => setScoringForm(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                              className="w-16 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs text-right"
                            />
                          ) : (
                            <span className="text-[#7c3aed] text-xs font-bold">{scoringConfig[key]}</span>
                          )}
                        </div>
                      ))}
                      <div className="mt-4 text-xs text-white/40">
                        <p>• Hot: score ≥ {scoringConfig?.hot_threshold || 70}</p>
                        <p>• Warm: score ≥ {scoringConfig?.warm_threshold || 25}</p>
                        <p>• Cold: score &lt; {scoringConfig?.warm_threshold || 25}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

              {/* Stats Bar */}
              {leadsStats && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                  <div className="glass rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-[#7c3aed]" />
                      <span className="text-white/50 text-xs">Total</span>
                    </div>
                    <div className="font-heading font-bold text-xl text-white">{leadsStats.total}</div>
                  </div>
                  <div className="glass rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Flame className="w-4 h-4 text-red-500" />
                      <span className="text-white/50 text-xs">Hot (70+)</span>
                    </div>
                    <div className="font-heading font-bold text-xl text-red-500">{leadsStats.hot}</div>
                  </div>
                  <div className="glass rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <ThermometerSun className="w-4 h-4 text-amber-500" />
                      <span className="text-white/50 text-xs">Warm (25-69)</span>
                    </div>
                    <div className="font-heading font-bold text-xl text-amber-500">{leadsStats.warm}</div>
                  </div>
                  <div className="glass rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Snowflake className="w-4 h-4 text-cyan-500" />
                      <span className="text-white/50 text-xs">Cold (&lt;25)</span>
                    </div>
                    <div className="font-heading font-bold text-xl text-cyan-500">{leadsStats.cold}</div>
                  </div>
                  <div className="glass rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="w-4 h-4 text-[#10B981]" />
                      <span className="text-white/50 text-xs">With Tester</span>
                    </div>
                    <div className="font-heading font-bold text-xl text-[#10B981]">{leadsStats.with_tester}</div>
                  </div>
                  <div className="glass rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />
                      <span className="text-white/50 text-xs">Duplicates</span>
                    </div>
                    <div className="font-heading font-bold text-xl text-[#F59E0B]">{leadsStats.possible_duplicates || 0}</div>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {/* Tier buttons */}
                  {['all', 'hot', 'warm', 'cold'].map(tier => (
                    <button
                      key={tier}
                      onClick={() => setFilters(prev => ({ ...prev, tier }))}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        filters.tier === tier
                          ? 'bg-[#7c3aed] text-white'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      {tier === 'all' ? 'All Leads' : TIER_CONFIG[tier.toUpperCase()]?.label || tier}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      showFilters ? 'bg-[#7c3aed] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <Filter size={14} />
                    More Filters
                  </button>
                  
                  {loading && <Loader2 className="w-5 h-5 text-[#7c3aed] animate-spin ml-2" />}
                </div>

                {/* Call form status filter */}
                <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="crm-form-status-filter">
                  <span className="text-white/40 text-xs mr-1">Call form:</span>
                  {[
                    ['', 'All'],
                    ['Qualified', `Qualified (${leadsStats?.qualified ?? 0})`],
                    ['Exploring', `Exploring (${leadsStats?.exploring ?? 0})`],
                    ['Incomplete', `Incomplete (${leadsStats?.incomplete ?? 0})`],
                  ].map(([value, label]) => (
                    <button
                      key={value || 'all'}
                      onClick={() => setFilters(prev => ({ ...prev, formStatus: value }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        filters.formStatus === value ? 'bg-[#7c3aed] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                      data-testid={`crm-form-status-${(value || 'all').toLowerCase()}`}
                    >
                      {label}
                    </button>
                  ))}
                  <label className="ml-2 flex items-center gap-2 text-xs text-white/60 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.showMerged}
                      onChange={(e) => setFilters(prev => ({ ...prev, showMerged: e.target.checked }))}
                      data-testid="crm-show-merged"
                    />
                    Show merged records ({leadsStats?.merged ?? 0})
                  </label>
                </div>

                {/* Extended filters */}
                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="glass rounded-xl p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
                        <div>
                          <label className="text-white/40 text-xs mb-1 block">Source</label>
                          <select
                            value={filters.source}
                            onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                          >
                            <option value="">All Sources</option>
                            <option value="strategy_lab">Strategy Lab</option>
                            <option value="tester_download">Tester</option>
                            <option value="call_lead">Call</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-white/40 text-xs mb-1 block">Status</label>
                          <select
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                          >
                            <option value="">All Status</option>
                            <option value="New">New</option>
                            <option value="Contacted">Contacted</option>
                            <option value="Qualified">Qualified</option>
                            <option value="Converted">Converted</option>
                            <option value="Lost">Lost</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-white/40 text-xs mb-1 block">Platform</label>
                          <select
                            value={filters.platform}
                            onChange={(e) => setFilters(prev => ({ ...prev, platform: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                          >
                            <option value="">All Platforms</option>
                            <option value="mt5">MT5</option>
                            <option value="nt8">NinjaTrader 8</option>
                            <option value="both">Both</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-white/40 text-xs mb-1 block">Language</label>
                          <select
                            value={filters.language}
                            onChange={(e) => setFilters(prev => ({ ...prev, language: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                          >
                            <option value="">All Languages</option>
                            <option value="english">English</option>
                            <option value="hindi">Hindi</option>
                            <option value="both">Both (Hinglish)</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-white/40 text-xs mb-1 block">From Date</label>
                          <input
                            type="date"
                            value={filters.dateFrom}
                            onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-white/40 text-xs mb-1 block">To Date</label>
                          <input
                            type="date"
                            value={filters.dateTo}
                            onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Leads List */}
              <div className="space-y-3">
                {leads.length === 0 ? (
                  <div className="glass rounded-xl p-8 text-center">
                    <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No leads found. Run the merge to populate unified leads.</p>
                  </div>
                ) : (
                  leads.map((lead) => {
                    const tierConfig = TIER_CONFIG[lead.tier] || TIER_CONFIG.COLD;
                    const TierIcon = tierConfig.icon;
                    const isExpanded = expandedLead === lead._id;
                    const hasDuplicates = lead.possible_duplicates?.length > 0;
                    
                    return (
                      <motion.div
                        key={lead._id}
                        layout
                        className={`glass rounded-xl border ${hasDuplicates ? 'border-[#F59E0B]/50' : 'border-white/5'} overflow-hidden`}
                        data-testid={`crm-lead-card-${lead._id}`}
                      >
                        {/* Lead Header */}
                        <div 
                          className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                          onClick={() => handleExpandLead(lead._id)}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Score badge */}
                              <div 
                                className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center ${tierConfig.bgColor}`}
                                style={{ color: tierConfig.color }}
                              >
                                <TierIcon size={16} />
                                <span className="text-xs font-bold">{lead.score}</span>
                              </div>
                              
                              {/* Name & Contact */}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-heading font-semibold text-white truncate">{lead.name || 'Unknown'}</h3>
                                  <FormStatusBadge status={lead.form_status} testId={`lead-form-status-badge-${lead._id}`} />
                                  {lead.merged_into && (
                                    <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-white/60" data-testid={`lead-merged-badge-${lead._id}`}>
                                      Merged into {leads.find(l => l._id === lead.merged_into)?.name || 'another lead'}
                                    </span>
                                  )}
                                  {hasDuplicates && (
                                    <span className="px-2 py-0.5 rounded-full text-xs bg-[#F59E0B]/20 text-[#F59E0B] flex items-center gap-1">
                                      <AlertTriangle size={10} />
                                      Possible duplicate
                                    </span>
                                  )}
                                </div>
                                <p className="text-white/40 text-sm truncate">
                                  {lead.emails?.[0] || ''} {lead.phones?.[0] ? `• ${lead.phones[0]}` : ''}
                                </p>
                              </div>
                              
                              {/* Sources badges */}
                              <div className="hidden md:flex gap-1">
                                {lead.sources?.includes('strategy_lab') && (
                                  <span className="px-2 py-1 rounded-full text-xs bg-[#7c3aed]/20 text-[#7c3aed]">Pine</span>
                                )}
                                {lead.sources?.includes('tester_download') && (
                                  <span className="px-2 py-1 rounded-full text-xs bg-[#10B981]/20 text-[#10B981]">Tester</span>
                                )}
                                {lead.sources?.includes('call_lead') && (
                                  <span className="px-2 py-1 rounded-full text-xs bg-[#F59E0B]/20 text-[#F59E0B]">Call</span>
                                )}
                                {lead.sources?.includes('ai_support') && (
                                  <span className="px-2 py-1 rounded-full text-xs bg-[#38bdf8]/20 text-[#38bdf8]" data-testid="lead-ai-support-badge">
                                    AI Support{lead.ai_support_escalated ? ' · WhatsApp' : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {lead.phones?.[0] && (
                                <a
                                  href={`https://wa.me/${lead.phones[0].replace(/[^0-9]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-2 rounded-lg bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 transition-colors"
                                >
                                  <MessageCircle size={16} />
                                </a>
                              )}
                              {isExpanded ? <ChevronUp size={18} className="text-white/40" /> : <ChevronDown size={18} className="text-white/40" />}
                            </div>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="border-t border-white/10"
                            >
                              <div className="p-4 bg-white/5">
                                <div className="grid md:grid-cols-2 gap-6">
                                  {/* Left: Lead Details */}
                                  <div className="space-y-4">
                                    <h4 className="font-heading font-semibold text-white text-sm flex items-center gap-2">
                                      <FileText size={14} />
                                      Lead Details
                                    </h4>
                                    
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                      <div>
                                        <span className="text-white/40">Score:</span>
                                        <p className="text-white font-bold" style={{ color: tierConfig.color }}>{lead.score} ({lead.tier})</p>
                                      </div>
                                      <div>
                                        <span className="text-white/40">CRM Status:</span>
                                        <p className="text-white">{lead.crm_status || 'New'}</p>
                                      </div>
                                      <div>
                                        <span className="text-white/40">Platform:</span>
                                        <p className="text-white uppercase">{lead.platform || lead.tester_platform || '-'}</p>
                                      </div>
                                      <div>
                                        <span className="text-white/40">Timezone:</span>
                                        <p className="text-white">{lead.timezone || '-'}</p>
                                      </div>
                                    </div>

                                    {/* Call request answers */}
                                    {(lead.call_count > 0 || lead.form_status) && <CallFormAnswers lead={lead} />}

                                    {/* Score Breakdown */}
                                    <div className="p-3 rounded-lg bg-white/5">
                                      <h5 className="text-white/60 text-xs font-semibold mb-2">Score Breakdown</h5>
                                      <div className="space-y-1 text-xs">
                                        {Object.entries(lead.score_breakdown || {}).map(([key, value]) => (
                                          <div key={key} className="flex justify-between">
                                            <span className="text-white/40">{key.replace(/_/g, ' ')}:</span>
                                            <span className={value >= 0 ? 'text-[#10B981]' : 'text-red-400'}>{value >= 0 ? '+' : ''}{value}</span>
                                          </div>
                                        ))}
                                        <div className="flex justify-between font-bold border-t border-white/10 pt-1 mt-1">
                                          <span className="text-white">Total:</span>
                                          <span className="text-white">{lead.score}</span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Possible Duplicates */}
                                    {hasDuplicates && (
                                      <div className="p-3 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30">
                                        <h5 className="text-[#F59E0B] text-xs font-semibold mb-2 flex items-center gap-2">
                                          <AlertTriangle size={12} />
                                          Possible Duplicate(s)
                                        </h5>
                                        {lead.possible_duplicate_details?.map((dup) => (
                                          <div key={dup._id} className="p-2 rounded bg-white/5 mb-2">
                                            <p className="text-white text-sm font-medium">{dup.name}</p>
                                            <p className="text-white/40 text-xs">{dup.emails?.[0]} • {dup.phones?.[0]}</p>
                                            <div className="flex gap-2 mt-2">
                                              <button
                                                onClick={() => mergeLeads(lead._id, dup._id)}
                                                className="flex-1 px-3 py-1.5 rounded-lg bg-[#10B981]/20 text-[#10B981] text-xs font-medium flex items-center justify-center gap-1 hover:bg-[#10B981]/30"
                                              >
                                                <Link2 size={12} />
                                                Merge these
                                              </button>
                                              <button
                                                onClick={() => markNotSame(lead._id, dup._id)}
                                                className="flex-1 px-3 py-1.5 rounded-lg bg-white/10 text-white/70 text-xs font-medium flex items-center justify-center gap-1 hover:bg-white/20"
                                              >
                                                <Unlink size={12} />
                                                Not the same person
                                              </button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  {/* Right: Activity Timeline */}
                                  <div className="space-y-4">
                                    <h4 className="font-heading font-semibold text-white text-sm flex items-center gap-2">
                                      <Activity size={14} />
                                      Activity Timeline
                                    </h4>
                                    
                                    {leadActivities[lead._id] ? (
                                      <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                                        {leadActivities[lead._id].map((activity, idx) => (
                                          <div key={idx} className="flex gap-3 p-2 rounded-lg bg-white/5">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                              ['pine_download', 'pine'].includes(activity.type) ? 'bg-[#7c3aed]/20' :
                                              activity.type === 'tester_download' ? 'bg-[#10B981]/20' :
                                              activity.type === 'ai_support_conversation' ? 'bg-[#38bdf8]/20' :
                                              'bg-[#F59E0B]/20'
                                            }`}>
                                              {activity.type === 'ai_support_conversation' && <Mic size={14} className="text-[#38bdf8]" />}
                                              {['pine_download', 'pine'].includes(activity.type) && <FileDown size={14} className="text-[#7c3aed]" />}
                                              {activity.type === 'tester_download' && <Zap size={14} className="text-[#10B981]" />}
                                              {activity.type === 'call_booking' && <PhoneCall size={14} className="text-[#F59E0B]" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-white text-sm capitalize">{activity.type?.replaceAll('_', ' ')}</p>
                                              <p className="text-white/40 text-xs truncate">
                                                {activity.strategy_name || activity.details?.platform || activity.details?.tag || activity.details?.language || ''}
                                              </p>
                                              <p className="text-white/30 text-xs">
                                                {activity.created_at ? new Date(activity.created_at).toLocaleString() : ''}
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-center py-4">
                                        <Loader2 className="w-5 h-5 text-[#7c3aed] animate-spin mx-auto" />
                                      </div>
                                    )}

                                    {/* Call Time, Meet link, Outcome, Notes */}
                                    {(lead.call_count > 0 || lead.form_status) && (
                                      <div className="mt-4 space-y-3" key={`${lead._id}-${lead.updated_at}`}>
                                        <CallTimeField lead={lead} authHeader={getAuthHeader()} onSaved={refreshCurrentTab} />
                                        <MeetLinkField lead={lead} authHeader={getAuthHeader()} onSaved={refreshCurrentTab} />
                                        <div>
                                          <label className="text-white/40 text-xs mb-1 block">Outcome</label>
                                          <OutcomeButtons lead={lead} onSelect={setCallOutcome} />
                                        </div>
                                        <NotesField lead={lead} authHeader={getAuthHeader()} onSaved={refreshCurrentTab} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* CALLS TAB */}
          {mainTab === 'calls' && (
            <>
              {/* Calls Stats Bar */}
              {callsStats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                  <div className="glass rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <PhoneCall className="w-4 h-4 text-[#7c3aed]" />
                      <span className="text-white/50 text-xs">Total Calls</span>
                    </div>
                    <div className="font-heading font-bold text-xl text-white">{callsStats.total}</div>
                  </div>
                  {Object.entries(CALL_TAB_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <div key={key} className="glass rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4" style={{ color: config.color }} />
                          <span className="text-white/50 text-xs">{config.label}</span>
                        </div>
                        <div className="font-heading font-bold text-xl" style={{ color: config.color }}>
                          {callsStats[key] || 0}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Calls Sub-tabs */}
              <div className="flex flex-wrap gap-2 mb-6">
                {Object.entries(CALL_TAB_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setCallsTabFilter(key)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        callsTabFilter === key
                          ? 'text-white'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                      style={callsTabFilter === key ? { backgroundColor: config.color } : {}}
                    >
                      <Icon size={14} />
                      {config.label}
                    </button>
                  );
                })}
                {loading && <Loader2 className="w-5 h-5 text-[#7c3aed] animate-spin ml-2" />}
              </div>

              {/* Call Leads List */}
              <div className="space-y-3">
                {callLeads.length === 0 ? (
                  <div className="glass rounded-xl p-8 text-center">
                    <PhoneCall className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No call leads in this category</p>
                  </div>
                ) : (
                  callLeads.map((lead) => {
                    const tabConfig = CALL_TAB_CONFIG[lead._call_tab] || CALL_TAB_CONFIG.awaiting;
                    const TabIcon = tabConfig.icon;
                    
                    return (
                      <div key={lead._id} className="glass rounded-xl p-4 border border-white/5" data-testid={`calls-row-${lead._id}`}>
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${tabConfig.color}20` }}
                            >
                              <TabIcon size={18} style={{ color: tabConfig.color }} />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <h3 className="font-heading font-semibold text-white">{lead.name}</h3>
                              <p className="text-white/40 text-sm">
                                {lead.language && <span>{LANGUAGE_LABELS[lead.language] || lead.language}</span>}
                                {lead.tag && <span className="ml-2 capitalize">• {lead.tag}</span>}
                                {lead.timezone && <span className="ml-2">• {lead.timezone}</span>}
                                {lead.email_status === 'sent' && <span className="ml-2 text-[#10B981]">• Email sent</span>}
                                {lead.email_status === 'failed' && <span className="ml-2 text-red-400">• Email failed</span>}
                              </p>
                              {lead.meet_link && (
                                <a href={lead.meet_link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#06b6d4] hover:underline" data-testid={`calls-meet-link-${lead._id}`}>
                                  {lead.meet_link}
                                </a>
                              )}
                            </div>
                            
                            <div className="text-right hidden sm:block">
                              {lead.call_time ? (
                                <p className="text-white text-sm" data-testid={`calls-time-${lead._id}`}>{formatIst(lead.call_time)}</p>
                              ) : (
                                <p className="text-white/40 text-sm">No time set</p>
                              )}
                              {lead.call_outcome && (
                                <span className={`text-xs capitalize px-2 py-0.5 rounded-full ${
                                  lead.call_outcome === 'interested' ? 'bg-[#10B981]/20 text-[#10B981]' :
                                  lead.call_outcome === 'not_now' ? 'bg-[#06b6d4]/20 text-[#06b6d4]' :
                                  'bg-[#F59E0B]/20 text-[#F59E0B]'
                                }`}>
                                  {OUTCOME_LABELS[lead.call_outcome] || lead.call_outcome}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            <a
                              href={`https://wa.me/${lead.phones?.[0]?.replace(/[^0-9]/g, '') || ''}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30"
                            >
                              <MessageCircle size={16} />
                            </a>
                            
                            <CallTimeField key={`${lead._id}-${lead.call_time}`} lead={lead} authHeader={getAuthHeader()} onSaved={fetchCalls} compact />
                            {(lead._call_tab === 'needs_outcome' || lead._call_tab === 'done' || lead._call_tab === 'booked') && (
                              <OutcomeButtons lead={lead} onSelect={setCallOutcome} />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}

          {mainTab === 'strategy_lab' && (
            <AdminLabPage key="lab-strategies" embeddedCredentials={savedCredentials()} embeddedTab="strategies" />
          )}

          {mainTab === 'ai_support' && <AdminSupportTab authHeader={getAuthHeader()} />}

          {mainTab === 'whatsapp' && (
            <AdminLabPage key="lab-whatsapp" embeddedCredentials={savedCredentials()} embeddedTab="whatsapp" />
          )}

          {showPasswordModal && (
            <ChangePasswordModal
              authHeader={getAuthHeader()}
              onClose={() => setShowPasswordModal(false)}
              onChanged={(newPassword) => {
                const username = savedCredentials().username;
                localStorage.setItem('crmAuth', btoa(`${username}:${newPassword}`));
                setCredentials({ username, password: newPassword });
                setShowPasswordModal(false);
                alert('Password updated.');
              }}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
}
